import posthog from "posthog-js"

type PostHogEnv = {
  DEV?: boolean
  MODE?: string
  VITE_PUBLIC_POSTHOG_PROJECT_TOKEN?: string
  VITE_PUBLIC_POSTHOG_HOST?: string
}

export type PostHogBrowserConfig = {
  apiKey: string
  apiHost: string
}

export function readPostHogBrowserConfig(
  env: PostHogEnv = import.meta.env as PostHogEnv,
): PostHogBrowserConfig | null {
  const apiKey = env.VITE_PUBLIC_POSTHOG_PROJECT_TOKEN?.trim()
  const apiHost = env.VITE_PUBLIC_POSTHOG_HOST?.trim()
  if (!apiKey || !apiHost) return null
  return { apiKey, apiHost }
}

/** Direct PostHog Cloud ingest hosts — anything else is treated as a reverse proxy. */
export function isDirectPostHogIngestHost(apiHost: string): boolean {
  if (apiHost.startsWith("/")) return false
  try {
    const { hostname } = new URL(apiHost)
    return (
      hostname === "us.i.posthog.com" ||
      hostname === "eu.i.posthog.com" ||
      hostname.endsWith(".i.posthog.com")
    )
  } catch {
    return false
  }
}

/**
 * When api_host is a reverse proxy, ui_host must stay on PostHog Cloud so
 * toolbar / dashboard links resolve correctly.
 * @see https://posthog.com/docs/libraries/js/config
 */
export function postHogUiHostFor(apiHost: string): string | undefined {
  return isDirectPostHogIngestHost(apiHost) ? undefined : "https://us.posthog.com"
}

function reportMissingConfiguration(env: PostHogEnv) {
  if (!env.DEV) return

  for (const variable of [
    "VITE_PUBLIC_POSTHOG_PROJECT_TOKEN",
    "VITE_PUBLIC_POSTHOG_HOST",
  ] as const) {
    if (!env[variable]?.trim()) {
      throw new Error(
        `${variable} variable required by PostHog is missing or un-configured, this causes events to be silently missed. This error stops appearing once ${variable} is configured`,
      )
    }
  }
}

let initialized = false
let identifiedDistinctId: string | null = null
let identifiedRole: string | undefined
let companyGroupId: string | null = null

export type PostHogUser = {
  distinctId: string
  email?: string | null
  name?: string | null
  role?: string
  portalStatus?: string
  candidateOnboarded?: boolean
  profileCompleteness?: number
  signupStage?: string
}

export type PostHogLogAttributes = Record<
  string,
  string | number | boolean | string[] | number[] | boolean[] | null | undefined
>

function lifecycleProps(user: PostHogUser): Record<string, string | number | boolean> {
  const props: Record<string, string | number | boolean> = {}
  if (user.role) props.role = user.role
  if (user.portalStatus) props.portal_status = user.portalStatus
  if (user.candidateOnboarded !== undefined) {
    props.candidate_onboarded = user.candidateOnboarded
  }
  if (user.profileCompleteness !== undefined) {
    props.profile_completeness = user.profileCompleteness
  }
  if (user.signupStage) props.signup_stage = user.signupStage
  return props
}

export function identifyPostHogUser(user: PostHogUser) {
  if (!initialized) return
  const { distinctId, email, name } = user
  const life = lifecycleProps(user)

  if (identifiedDistinctId !== distinctId) {
    if (identifiedDistinctId !== null) posthog.reset()
    companyGroupId = null
    posthog.identify(distinctId, {
      ...(email ? { email } : {}),
      ...(name ? { name } : {}),
      ...life,
    })
    identifiedDistinctId = distinctId
    identifiedRole = user.role
    return
  }

  if (Object.keys(life).length > 0) {
    posthog.people.set(life)
    if (user.role) identifiedRole = user.role
  }
}

/** B2B group analytics — company is the canonical org unit. */
export function setPostHogCompanyGroup(companyId: string | null | undefined) {
  if (!initialized) return
  const next = companyId?.trim() || null
  if (next === companyGroupId) return
  companyGroupId = next
  if (next) posthog.group("company", next)
}

export function resetPostHogUser() {
  if (initialized) posthog.reset()
  identifiedDistinctId = null
  identifiedRole = undefined
  companyGroupId = null
}

export function capturePostHogEvent(
  event: string,
  properties?: Record<string, unknown>,
) {
  if (initialized) posthog.capture(event, properties)
}

/** Structured OTLP logs for the PostHog Logs product (not analytics events). */
export function logPostHog(
  level: "debug" | "info" | "warn" | "error" | "fatal",
  message: string,
  attributes?: PostHogLogAttributes,
) {
  if (!initialized) return
  const attrs = attributes
    ? Object.fromEntries(
        Object.entries(attributes).filter(
          ([, v]) => v !== undefined && v !== null,
        ),
      )
    : undefined
  posthog.logger[level](message, attrs)
}

export function initPostHog(
  env: PostHogEnv = import.meta.env as PostHogEnv,
): boolean {
  if (initialized) return true
  const config = readPostHogBrowserConfig(env)
  if (!config) {
    reportMissingConfiguration(env)
    return false
  }

  // Managed reverse proxy (e.g. https://z.hsrfounders.club) or a same-origin
  // path avoids ad blockers. ui_host stays on PostHog Cloud for toolbar links.
  const uiHost = postHogUiHostFor(config.apiHost)
  posthog.init(config.apiKey, {
    api_host: config.apiHost,
    ...(uiHost ? { ui_host: uiHost } : {}),
    defaults: "2026-05-30",
    person_profiles: "identified_only",
    capture_pageleave: true,
    capture_performance: true,
    capture_exceptions: true,
    // Session replay is enabled project-side; keep form fields masked —
    // this portal handles resumes, emails, and other candidate PII.
    session_recording: {
      maskAllInputs: true,
    },
    // Populates PostHog Logs (OTLP). Prefer posthog.logger / logPostHog —
    // do not enable captureConsoleLogs (forwards secrets from console.*).
    logs: {
      serviceName: "hsrfc-hire",
      environment: env.MODE === "production" ? "production" : "development",
    },
  })
  initialized = true
  logPostHog("info", "posthog_initialized", {
    service: "hsrfc-hire",
    environment: env.MODE === "production" ? "production" : "development",
    proxied: Boolean(uiHost),
  })
  return true
}

export { posthog as posthogClient }
